import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PatientDocumentResponse, QueryPatientDocument } from '../../contracts';
import { GET_PATIENT_DOCUMENTS } from '../../contracts';
import { getPatientDocuments } from '../api/patients';

// Readiness polling cadence + a hard upper bound (~5 min) so a doc that never
// reaches a terminal state can't poll indefinitely while the profile is open.
const READINESS_POLL_INTERVAL_MS = 2500;
const MAX_READINESS_POLLS = 120;

/**
 * Fetch the documents attached to a patient, newest first.
 *
 * The query key includes the patient id so multiple drawers / tabs remain
 * independent. A transient `loadingProgress` field is preserved across
 * refetches so in-flight uploads continue to show progress until the server
 * confirms completion.
 */
export function useQueryPatientDocuments(
  patientProfileId: string | null | undefined,
  /**
   * The app's `state.auth.token` read, composed app-side and passed in
   * (cohort-C). A package hook cannot reach the Rematch store, and injecting
   * the store would relocate the coupling rather than remove it.
   */
  isAuthenticated = true
) {
  const queryClient = useQueryClient();

  return useQuery<QueryPatientDocument[] | undefined>({
    queryKey: [GET_PATIENT_DOCUMENTS, { patientProfileId }],
    enabled: isAuthenticated && !!patientProfileId,
    // Poll while any document is still being processed by the BE-1c
    // extract→summarize pipeline (explicitly not ready and not failed), then
    // stop on its own once every row settles. `is_ready === false` (not just
    // falsy) avoids polling forever on optimistic upload entries that have no
    // pipeline state yet.
    //
    // The BE guarantees a terminal state (READY / FAIL_TO_*, and a failed
    // trigger is marked terminal too), so this normally self-terminates. The
    // dataUpdateCount cap is belt-and-braces: if a doc ever wedged non-terminal
    // (worker lost mid-flight, etc.), stop polling after ~5 min
    // (POLL_INTERVAL_MS * MAX_POLLS) rather than hammering 2.5s forever while
    // the profile stays open. 5 min is well past the worst-case (~90s + retries),
    // so it won't cut off a legitimately slow doc.
    refetchInterval: (query) => {
      const stillProcessing = query.state.data?.some(
        (d) => d.is_ready === false && !d.failure_reason
      );
      if (!stillProcessing) return false;
      return query.state.dataUpdateCount < MAX_READINESS_POLLS
        ? READINESS_POLL_INTERVAL_MS
        : false;
    },
    queryFn: async () => {
      if (!patientProfileId) return [];
      const { data } = await getPatientDocuments(patientProfileId);
      const oldData = queryClient.getQueryData<QueryPatientDocument[]>([
        GET_PATIENT_DOCUMENTS,
        { patientProfileId },
      ]);
      return data.items.map((d: PatientDocumentResponse) => {
        // Only carry over `loadingProgress` when the previous entry is still
        // truly in flight (< 100). Otherwise a completed upload's terminal
        // 100 sticks around and keeps the card in "uploading" shape forever
        // — no click-to-preview, no delete.
        const oldProgress = oldData?.find(
          (f) => f.id === d.id || f.file_name === d.file_name
        )?.loadingProgress;
        return {
          ...d,
          loadingProgress:
            typeof oldProgress === 'number' && oldProgress < 100
              ? oldProgress
              : undefined,
        };
      });
    },
  });
}
